import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { InvoiceDisputeBadge, InvoiceStatusBadge } from './InvoiceStatusBadge';
import { SendForPaymentDialog, copyPaymentLink } from './SendForPaymentDialog';
import { EmailInvoiceDialog, canEmailInvoice } from './EmailInvoiceDialog';
import { InvoiceWithRelations } from '@/types/invoice';
import { MoreHorizontal, Eye, Pencil, Send, Check, X, Printer, ExternalLink, Trash2, CreditCard, Link as LinkIcon, Smartphone, Mail } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useTechUsers } from '@/hooks/useReferenceData';
import { cn } from '@/lib/utils';
import { useMarkInvoiceSent } from '@/hooks/useInvoices';
import { InvoiceActionConfirmDialog, type InvoiceConfirmAction } from './InvoiceActionConfirmDialog';
import { MarkInvoicePaidDialog } from './MarkInvoicePaidDialog';
import { EditInvoiceDialog } from './EditInvoiceDialog';
import { CANCELLABLE_INVOICE_STATUSES, canDeleteInvoice } from '@/types/invoice';
import { useVisibility } from '@/hooks/useVisibility';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import { formatInTz } from '@/utils/timezone';

/** Square invoice states after which the paid status is final and no longer synced. */
const PROVIDER_TERMINAL_STATUSES = ['PAID', 'REFUNDED', 'CANCELED'];
const TERMINAL_STATUSES = ['paid', 'cancelled', 'refunded'];

interface InvoicesTableViewProps {
  invoices: InvoiceWithRelations[];
  isLoading?: boolean;
  onViewInvoice: (invoice: InvoiceWithRelations) => void;
  onPrintInvoice: (invoice: InvoiceWithRelations) => void;
}

export function InvoicesTableView({
  invoices,
  isLoading,
  onViewInvoice,
  onPrintInvoice,
}: InvoicesTableViewProps) {
  const navigate = useNavigate();
  const markSent = useMarkInvoiceSent();
  const [confirm, setConfirm] = useState<{ action: InvoiceConfirmAction; invoice: InvoiceWithRelations } | null>(null);
  const [markPaidFor, setMarkPaidFor] = useState<InvoiceWithRelations | null>(null);

  const { formatMoney: formatCurrency, formatShortDate: formatDate, timezone } = useCompanyLocale();
  const { canSeeCompanyFinancials } = useVisibility();
  const { data: techUsers } = useTechUsers();
  const [sendFor, setSendFor] = useState<InvoiceWithRelations | null>(null);
  const [emailFor, setEmailFor] = useState<InvoiceWithRelations | null>(null);
  // Edit from the row menu, so staff don't have to open the quick view first.
  // Paid / refunded invoices open with the total locked (the dialog enforces it).
  const [editFor, setEditFor] = useState<InvoiceWithRelations | null>(null);

  // "Today" in the company timezone; due dates are calendar dates so compare at day granularity.
  const todayKey = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  const isOverdue = (invoice: InvoiceWithRelations) => {
    if (TERMINAL_STATUSES.includes(invoice.status)) return false;
    return formatDocDate(invoice.due_date, 'yyyy-MM-dd', timezone) < todayKey;
  };

  const canSendForPayment = (invoice: InvoiceWithRelations) =>
    ['draft', 'sent', 'overdue'].includes(invoice.status) && !invoice.provider_invoice_id;

  // Once published to Square, paid status comes from the webhook until the
  // provider reaches a terminal state.
  const paidSyncedFromProvider = (invoice: InvoiceWithRelations) =>
    !!invoice.provider_invoice_id &&
    !PROVIDER_TERMINAL_STATUSES.includes((invoice.provider_invoice_status ?? '').toUpperCase());

  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice #</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Job</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead className="text-right">Paid</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Due</TableHead>
            <TableHead className="w-[90px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[1, 2, 3, 4, 5].map((i) => (
            <TableRow key={i}>
              <TableCell><div className="h-4 bg-muted rounded w-20 animate-pulse"></div></TableCell>
              <TableCell><div className="h-4 bg-muted rounded w-32 animate-pulse"></div></TableCell>
              <TableCell><div className="h-4 bg-muted rounded w-16 animate-pulse"></div></TableCell>
              <TableCell><div className="h-4 bg-muted rounded w-20 animate-pulse ml-auto"></div></TableCell>
              <TableCell><div className="h-4 bg-muted rounded w-20 animate-pulse ml-auto"></div></TableCell>
              <TableCell><div className="h-6 bg-muted rounded w-16 animate-pulse"></div></TableCell>
              <TableCell><div className="h-4 bg-muted rounded w-24 animate-pulse"></div></TableCell>
              <TableCell></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-muted-foreground">No invoices found</p>
      </div>
    );
  }

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Invoice #</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Job</TableHead>
          <TableHead className="text-right">Amount</TableHead>
          <TableHead className="text-right">Paid</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Due</TableHead>
          <TableHead className="w-[90px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invoices.map((invoice) => (
          <TableRow
            key={invoice.id}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onViewInvoice(invoice)}
          >
            <TableCell className="font-medium text-muted-foreground">{invoice.invoice_number}</TableCell>
            <TableCell>
              <span className="font-medium">{invoice.customer_name?.trim() || '-'}</span>
            </TableCell>
            <TableCell>
              <span className="text-muted-foreground">{invoice.job?.job_number || '—'}</span>
            </TableCell>
            <TableCell className="text-right font-medium tabular-nums">
              {formatCurrency(invoice.total_amount)}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {invoice.amount_paid > 0 ? (
                <span className="font-medium text-success">{formatCurrency(invoice.amount_paid)}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-1.5">
                <InvoiceStatusBadge status={isOverdue(invoice) && invoice.status === 'sent' ? 'overdue' : invoice.status} />
                <InvoiceDisputeBadge state={invoice.dispute_state} />
                {!!invoice.created_by && techUsers?.has(invoice.created_by) && (
                  <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 text-info border-info/30" title="Closed from the Steddy app on site">
                    <Smartphone className="h-3 w-3" />
                    Field
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className={cn('tabular-nums', isOverdue(invoice) && 'font-medium text-destructive')}>
              {formatDate(invoice.due_date)}
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPrintInvoice(invoice)}
                  title="Print / PDF"
                >
                  <Printer className="h-4 w-4" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onViewInvoice(invoice)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </DropdownMenuItem>
                    {invoice.status !== 'cancelled' && (
                      <DropdownMenuItem onClick={() => setEditFor(invoice)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => onPrintInvoice(invoice)}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print / PDF
                    </DropdownMenuItem>
                    {invoice.customer_id && (
                      <DropdownMenuItem onClick={() => navigate(`/customers/${invoice.customer_id}`)}>
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Customer
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {canEmailInvoice(invoice) && (
                      <DropdownMenuItem onClick={() => setEmailFor(invoice)}>
                        <Mail className="h-4 w-4 mr-2" />
                        Email invoice
                      </DropdownMenuItem>
                    )}
                    {canSendForPayment(invoice) && (
                      <DropdownMenuItem onClick={() => setSendFor(invoice)}>
                        <CreditCard className="h-4 w-4 mr-2" />
                        Send for payment
                      </DropdownMenuItem>
                    )}
                    {invoice.payment_url && (
                      <DropdownMenuItem onClick={() => void copyPaymentLink(invoice.payment_url as string)}>
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Copy pay link
                      </DropdownMenuItem>
                    )}
                    {invoice.status === 'draft' && (
                      <DropdownMenuItem onClick={() => markSent.mutate(invoice.id)}>
                        <Send className="h-4 w-4 mr-2" />
                        Mark as Sent
                      </DropdownMenuItem>
                    )}
                    {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                      <DropdownMenuItem
                        onClick={() => setMarkPaidFor(invoice)}
                        disabled={paidSyncedFromProvider(invoice)}
                        title={paidSyncedFromProvider(invoice) ? 'Paid status is synced from Square' : undefined}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Mark as Paid
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    {CANCELLABLE_INVOICE_STATUSES.includes(invoice.status) && (
                      <DropdownMenuItem
                        onClick={() => setConfirm({ action: 'cancel', invoice })}
                        className="text-warning focus:text-warning"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel Invoice
                      </DropdownMenuItem>
                    )}
                    {canDeleteInvoice(invoice, canSeeCompanyFinancials) && (
                      <DropdownMenuItem
                        onClick={() => setConfirm({ action: 'delete', invoice })}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Invoice
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>

    <SendForPaymentDialog
      invoice={sendFor}
      open={sendFor !== null}
      onOpenChange={(open) => !open && setSendFor(null)}
    />
    <EmailInvoiceDialog
      invoice={emailFor}
      open={emailFor !== null}
      onOpenChange={(open) => !open && setEmailFor(null)}
    />
      <InvoiceActionConfirmDialog
        action={confirm?.action ?? null}
        invoice={confirm?.invoice ?? null}
        onOpenChange={(next) => !next && setConfirm(null)}
      />
      <MarkInvoicePaidDialog
        invoice={markPaidFor}
        open={!!markPaidFor}
        onOpenChange={(next) => !next && setMarkPaidFor(null)}
      />
      <EditInvoiceDialog
        invoiceId={editFor?.id ?? null}
        open={!!editFor}
        onOpenChange={(next) => !next && setEditFor(null)}
      />
    </>
  );
}
